/**
 * The form Flux Agent is filling in, shown in the chat. While the agent writes a create or edit
 * of an activity, bundle, project or custom field, the card lists every field of the app's own
 * form and fills them in as the arguments stream; afterwards it shows the saved result with a
 * link to open the item. Field lists, labels and formatted values come from the server
 * (`flux_agent::forms`); this component only renders them.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentFormCard
 */

import { Link } from 'react-router-dom';
import { AlertCircle, Check, ExternalLink, Loader2, PenLine } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { FluxAgentForm, FluxAgentFormField } from '@/lib/fluxAgentApi';
import { cn } from '@/lib/utils';

/** Inputs for {@link FluxAgentFormCard}. */
export interface FluxAgentFormCardProps {
  /** The form to render, live (`filling`) or final. */
  form: FluxAgentForm;
  /** One-line outcome from the action card, shown under the heading once the call finished. */
  summary?: string;
}

/** Status line text for each form status. */
const STATUS_LABEL: Record<FluxAgentForm['status'], string> = {
  filling: 'Filling in…',
  completed: 'Saved',
  failed: 'Not saved',
  pending: 'Waiting for you',
};

/** One label/value row; unfilled fields show a dash, the field being written shows a caret. */
function FieldRow({ field }: { /** The server-shaped field to show. */ field: FluxAgentFormField }) {
  return (
    <div className="contents">
      <dt className="text-muted-foreground flex items-center gap-1.5 py-0.5">
        {field.label}
        {field.badge && (
          <Badge
            variant={field.badge === 'required' ? 'default' : 'secondary'}
            className="h-4 px-1.5 text-[10px] font-medium"
          >
            {field.badge === 'required' ? 'Required' : 'Optional'}
          </Badge>
        )}
      </dt>
      <dd
        className={cn(
          'min-w-0 break-words py-0.5',
          field.filled ? 'text-foreground font-medium' : 'text-muted-foreground/60'
        )}
        data-filled={field.filled ? 'true' : 'false'}
      >
        {field.filled ? field.display : '—'}
        {field.active && (
          <span
            className="bg-primary ml-0.5 inline-block h-3 w-0.5 animate-pulse align-middle motion-reduce:animate-none"
            aria-hidden="true"
          />
        )}
      </dd>
    </div>
  );
}

/** Render one Flux Agent form: heading and status, every field, custom fields with badges, and an Open link. */
export function FluxAgentFormCard({ form, summary }: FluxAgentFormCardProps) {
  const filled = form.fields.filter(field => field.filled).length;
  const StatusIcon =
    form.status === 'filling'
      ? Loader2
      : form.status === 'failed'
        ? AlertCircle
        : form.status === 'completed'
          ? Check
          : PenLine;
  return (
    <section
      className={cn(
        'rounded-lg border px-3 py-2.5 text-xs',
        form.status === 'failed'
          ? 'border-destructive/30 bg-destructive/5'
          : 'border-primary/25 bg-primary/5'
      )}
      aria-label={`${form.heading}: ${STATUS_LABEL[form.status]}`}
      aria-live={form.status === 'filling' ? 'polite' : undefined}
      aria-busy={form.status === 'filling'}
      data-testid="flux-agent-form-card"
      data-form-status={form.status}
    >
      <header className="mb-1.5 flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold">
          <StatusIcon
            className={cn(
              'h-3.5 w-3.5 shrink-0',
              form.status === 'filling' && 'animate-spin motion-reduce:animate-none',
              form.status === 'failed' ? 'text-destructive/80' : 'text-primary'
            )}
            aria-hidden="true"
          />
          {form.heading}
        </p>
        <span className="text-muted-foreground">
          {STATUS_LABEL[form.status]}
          {form.status === 'filling' && ` · ${filled} of ${form.fields.length}`}
        </span>
      </header>
      {summary && form.status !== 'filling' && (
        <p
          className={cn(
            'mb-1.5 break-words',
            form.status === 'failed' ? 'text-destructive/90' : 'text-muted-foreground'
          )}
        >
          {summary}
        </p>
      )}
      <dl className="grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-3">
        {form.fields.map(field => (
          <FieldRow key={field.key} field={field} />
        ))}
      </dl>
      {form.customFields && form.customFields.length > 0 && (
        <>
          <p className="text-muted-foreground mb-0.5 mt-2 font-semibold">Custom fields</p>
          <dl className="grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-3">
            {form.customFields.map(field => (
              <FieldRow key={field.key} field={field} />
            ))}
          </dl>
        </>
      )}
      {form.status === 'completed' && form.link && (
        <Link
          to={form.link}
          className="text-primary mt-2 inline-flex items-center gap-1 font-medium hover:underline"
        >
          Open {form.kind === 'custom_field' ? 'activities' : form.kind}
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </Link>
      )}
    </section>
  );
}
